import { useEffect } from 'react';

export type ConfirmStyle = 'modal' | 'actionSheet';

interface ConfirmProps {
    isOpen: boolean;
    title?: string;
    message: string;
    confirmText: string;
    cancelText: string;
    confirmButtonColor: 'primary' | 'danger' | 'warning';
    style: ConfirmStyle;
    onConfirm: () => void;
    onCancel: () => void;
}

export default function Confirm({
    isOpen,
    title,
    message,
    confirmText,
    cancelText,
    confirmButtonColor,
    style,
    onConfirm,
    onCancel,
}: ConfirmProps) {
    // ESC 키로 취소
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onCancel();
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onCancel]);

    if (!isOpen) return null;

    const getConfirmButtonStyle = () => {
        const baseStyle = 'flex-1 py-3 rounded-lg font-semibold transition-colors';

        switch (confirmButtonColor) {
            case 'danger':
                return `${baseStyle} bg-red-600 text-white hover:bg-red-700`;
            case 'warning':
                return `${baseStyle} bg-orange-600 text-white hover:bg-orange-700`;
            case 'primary':
            default:
                return `${baseStyle} text-white hover:opacity-90`;
        }
    };

    // ========== 옵션 A: 중앙 모달 스타일 ==========
    if (style === 'modal') {
        return (
            <>
                {/* 오버레이 */}
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                    onClick={onCancel}
                >
                    {/* 모달 */}
                    <div
                        className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* 타이틀 */}
                        {title && (
                            <div className="flex items-center justify-center mb-4">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center"
                                    style={{
                                        backgroundColor: confirmButtonColor === 'danger' ? '#FEE2E2' : '#F1EEFC',
                                    }}
                                >
                                    <i
                                        className={`text-2xl ${confirmButtonColor === 'danger'
                                                ? 'ri-error-warning-line text-red-600'
                                                : 'ri-question-line'
                                            }`}
                                        style={confirmButtonColor !== 'danger' ? { color: '#7966FF' } : {}}
                                    ></i>
                                </div>
                            </div>
                        )}

                        {/* 메시지 */}
                        <div className="text-center mb-6">
                            {title && <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>}
                            <p className="text-gray-600 whitespace-pre-line">{message}</p>
                        </div>

                        {/* 버튼 */}
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={onCancel}
                                className="flex-1 py-3 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={onConfirm}
                                className={getConfirmButtonStyle()}
                                style={
                                    confirmButtonColor === 'primary'
                                        ? { backgroundColor: '#7966FF' }
                                        : undefined
                                }
                            >
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ========== 옵션 B: 하단 액션시트 스타일 ==========
    if (style === 'actionSheet') {
        return (
            <>
                {/* 오버레이 */}
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-50"
                    onClick={onCancel}
                ></div>

                {/* 액션시트 */}
                <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl p-4 shadow-xl animate-slide-up">
                    {/* 핸들 바 */}
                    <div className="flex justify-center mb-4">
                        <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
                    </div>

                    {/* 타이틀 & 메시지 */}
                    <div className="mb-4 px-2">
                        {title && (
                            <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">{title}</h3>
                        )}
                        <p className="text-gray-600 text-center whitespace-pre-line">{message}</p>
                    </div>

                    {/* 버튼들 */}
                    <div className="space-y-2">
                        <button
                            onClick={onConfirm}
                            className={`w-full py-4 rounded-lg font-semibold text-lg transition-colors ${confirmButtonColor === 'danger'
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : confirmButtonColor === 'warning'
                                        ? 'bg-orange-600 text-white hover:bg-orange-700'
                                        : 'text-white hover:opacity-90'
                                }`}
                            style={
                                confirmButtonColor === 'primary'
                                    ? { backgroundColor: '#7966FF' }
                                    : undefined
                            }
                        >
                            {confirmText}
                        </button>
                        <button
                            onClick={onCancel}
                            className="w-full py-4 rounded-lg font-semibold text-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                            {cancelText}
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return null;
}